import React from 'react';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6" data-testid="refund-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-3 mb-8">
          <RotateCcw size={32} className="text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="refund-page-title">Refund Policy</h1>
            <p className="text-sm text-gray-500">Last updated: August 12th, 2026</p>
          </div>
        </div>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-bold text-gray-900">1. Our Commitment</h2>
            <p className="text-gray-700">At HiBFF.com, we want you to feel confident in your decision to join our community. If you&apos;re not completely satisfied with your subscription, we offer a <strong>30-day full refund guarantee</strong> on all paid plans.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900">2. 30-Day Refund Window</h2>
            <p className="text-gray-700">You may request a full refund within 30 days of your initial purchase date for any of our paid subscription plans:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li><strong>Steady</strong> – £16/month</li>
              <li><strong>Together</strong> – £28/month</li>
              <li><strong>Brave</strong> – £188/year (£15.67/month, billed annually)</li>
            </ul>
            <p className="text-gray-700">Refunds are calculated from the date of your first payment. If you are outside the 30-day window, we may still consider refunds on a case-by-case basis but are not obligated to do so.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900">3. How Refunds Are Processed</h2>
            <p className="text-gray-700">All payments on HiBFF.com are processed by Paddle.com Market Limited, our Merchant of Record. This means:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Paddle handles all payment processing, tax calculations, and compliance.</li>
              <li>Paddle is responsible for collecting and remitting all applicable sales taxes (VAT, GST, etc.).</li>
              <li>When you make a purchase, your payment contract is with Paddle, not HiBFF.com.</li>
              <li>All refunds are ultimately processed by Paddle in accordance with their policies.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900">4. How to Request a Refund</h2>
            <p className="text-gray-700">To request a refund, please contact Paddle&apos;s buyer support team directly:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Paddle Support Portal: <a href="https://www.paddle.com/support" target="_blank" rel="noopener noreferrer" className="text-purple-600">https://www.paddle.com/support</a></li>
              <li>Email: <a href="mailto:buyer-support@paddle.com" className="text-purple-600">buyer-support@paddle.com</a></li>
            </ul>
            <p className="text-gray-700">When contacting support, please have the following information ready:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Your email address used for the purchase</li>
              <li>Your order/invoice number (sent to you by Paddle at the time of purchase)</li>
              <li>The name of the plan you subscribed to</li>
            </ul>
            <p className="text-gray-700">You may also contact HiBFF.com support at <a href="mailto:support@hibff.com" className="text-purple-600">support@hibff.com</a> for assistance with your refund request, but all refunds are processed by Paddle.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900">5. Important Notes</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Paddle reserves the right to deny refund requests in cases of fraud, abuse, or violation of our Terms &amp; Conditions.</li>
              <li>Refunds may take 5-10 business days to appear on your statement, depending on your payment provider.</li>
              <li>Refunds are issued to the original payment method used at the time of purchase.</li>
              <li>Partial refunds may be issued at Paddle&apos;s discretion (e.g., for pro-rated cancellations mid-cycle).</li>
              <li>This refund policy applies only to paid subscriptions, not to the free &quot;Entry&quot; plan.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900">6. Paddle&apos;s Role as Merchant of Record</h2>
            <p className="text-gray-700">HiBFF.com uses Paddle.com Market Limited as our Merchant of Record (MoR). This means:</p>
            <p className="text-gray-700 italic border-l-4 border-purple-200 pl-4">&quot;Our order process is conducted by our online reseller Paddle.com Market Limited. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.&quot;</p>
            <p className="text-gray-700">Paddle&apos;s full terms and privacy policy are available at:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li><a href="https://www.paddle.com/legal/terms" target="_blank" rel="noopener noreferrer" className="text-purple-600">https://www.paddle.com/legal/terms</a></li>
              <li><a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600">https://www.paddle.com/legal/privacy</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900">7. Questions?</h2>
            <p className="text-gray-700">If you have any questions about this refund policy, please contact us at:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Email: <a href="mailto:refund@hibff.com" className="text-purple-600">refund@hibff.com</a></li>
              <li>Response Time: Within 24-48 hours</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900">8. Changes to This Policy</h2>
            <p className="text-gray-700">HiBFF.com reserves the right to update or modify this Refund Policy at any time without prior notice. Any changes will be posted on this page with an updated &quot;Last Updated&quot; date.</p>
          </section>

          <p className="text-gray-600 italic text-center pt-4" style={{ fontFamily: 'Georgia, serif' }}>HiBFF.com — Brave takes courage. Courage builds confidence. Confidence builds character. And character is the foundation of friends forever.</p>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
